import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getProphetById, getProphetChapters, deleteChapter, reorderChapter } from '../../services/prophetService';
import ProphetChapterFormModal from './ProphetChapterFormModal';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Box, makeStyles, CircularProgress, Snackbar, Tooltip, Typography, Chip
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  deleteBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  orderBadge: {
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    padding: '4px 8px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  },
}));

export default function ProphetChapterListPage() {
  const classes = useStyles();
  const history = useHistory();
  const { prophetId } = useParams();
  
  const [prophet, setProphet] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pData, chData] = await Promise.all([
        getProphetById(prophetId),
        getProphetChapters(prophetId),
      ]);
      setProphet(pData);
      setChapters(chData);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load chapters' });
    }
    setLoading(false);
  }, [prophetId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteChapter(prophetId, deleteTarget.id);
      setSnackbar({ open: true, message: 'Chapter deleted successfully' });
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete chapter' });
    }
  };

  const handleReorder = async (item, direction) => {
    const idx = chapters.indexOf(item);
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= chapters.length) return;
    const target = chapters[targetIdx];
    try {
      await Promise.all([
        reorderChapter(prophetId, item.id, target.orderIndex),
        reorderChapter(prophetId, target.id, item.orderIndex),
      ]);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reorder chapters' });
    }
  };

  const prophetTitle = prophet ? (prophet.name?.en || prophet.arabicName || 'Prophet') : 'Prophet';

  return (
    <AdminLayout title={`Chapters - ${prophetTitle}`}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => history.push('/prophets')}
        style={{ marginBottom: 16 }}
      >
        Back to Prophets
      </Button>

      <div className={classes.toolbar}>
        <Typography variant="body1" color="textSecondary">
          Manage chapters for <strong>{prophetTitle}</strong>
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 16 }} flexWrap="wrap">
          <Chip
            label={`Total Chapters: ${chapters.length}`}
            className={classes.countChip}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { setSelectedChapterId(null); setModalOpen(true); }}
            disableElevation
          >
            Add Chapter
          </Button>
        </Box>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} thickness={4} />
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="60">Order</TableCell>
                <TableCell width="30%">Title (EN)</TableCell>
                <TableCell width="30%">Title (BN)</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chapters.map((ch) => (
                <TableRow key={ch.id}>
                  <TableCell>
                    <span className={classes.orderBadge}>{ch.orderIndex}</span>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {ch.title?.en || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {ch.title?.bn || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" style={{ gap: 6 }}>
                      <Tooltip title="Move Up">
                        <IconButton className={classes.actionBtn} onClick={() => handleReorder(ch, 'up')}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move Down">
                        <IconButton className={classes.actionBtn} onClick={() => handleReorder(ch, 'down')}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton className={classes.actionBtn} onClick={() => { setSelectedChapterId(ch.id); setModalOpen(true); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton className={classes.deleteBtn} onClick={() => setDeleteTarget(ch)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {chapters.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ padding: "48px 0" }}>
                    No chapters found for this prophet. Click "Add Chapter" to create the first one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Chapter"
        message="Are you sure you want to delete this chapter?"
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ProphetChapterFormModal
        open={modalOpen}
        prophetId={prophetId}
        chapterId={selectedChapterId}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          setModalOpen(false);
          loadData();
        }}
      />

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}
